import '../App.css';
import '../Feed.css';
import { headerStyling, subheaderStyling } from '../App';
import scribble from '../images/scribble2.png';
import { Navigate, useParams } from 'react-router';
import feedArchiveData from '../feed-data.json'

const archiveData: Record<string, any | undefined> = feedArchiveData

const TIDAL_REGEX = /(https:\/\/embed\.tidal\.com\/).*/
const SPOTIFY_REGEX = /(https:\/\/open\.spotify\.com\/embed).*/

function GenericPlaylistWidgetEmbed({ playlistLink }: { playlistLink: string }) {
    if (playlistLink.match(TIDAL_REGEX)) {
        return <TidalPlaylistWidgetEmbed playlistLink={playlistLink} />
    } else if (playlistLink.match(SPOTIFY_REGEX)) {
        return <SpotifyPlaylistWidgetEmbed playlistLink={playlistLink} />
    } else {
        return <></>
    }
}

function SpotifyPlaylistWidgetEmbed({ playlistLink }: { playlistLink: string }) {
    return (
        <>
            <iframe
                className="Feed-playlist"
                src={playlistLink}
                height="352"
                frameBorder="0"
                allowFullScreen={false}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="Spotify Playlist">
            </iframe>
        </>
    )
}

function TidalPlaylistWidgetEmbed({ playlistLink }: { playlistLink: string }) {
    return (
        <>
            <iframe
                src={playlistLink}
                className="Feed-playlist"
                width="500"
                height="275"
                frameBorder="0"
                allow="encrypted-media; fullscreen; clipboard-write https://embed.tidal.com; web-share" sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox" title="TIDAL Embed Player"
            />
        </>
    )
}

interface YearArchiveType {
    books: any[],
    filmsOld: any[],
    music: any,
    tvBlurb: string | undefined,
    currentlyReading: any | undefined
}

function ArchivedFeed({ year }: { year: string }) {
    const data = archiveData.archive[year] as YearArchiveType
    if (data === undefined) {
        return (
            <Navigate to="/feed" />
        )
    }

    const yearString = (year === "present") ? "this year" : year

    return (
        <div>
            <p style={subheaderStyling}>My media consumption in {yearString}</p>
            <hr />
            <p style={headerStyling}>Reading</p>
            <p>The best books I read:</p>
            {
                data.books.map((book) => {
                    return (
                        <>
                            <p style={subheaderStyling}>
                                <i>{book.title}</i> by {book.author}
                            </p>
                            <p dangerouslySetInnerHTML={{ __html: book.blurb }} />
                        </>
                    )
                })
            }
            <hr />
            <p style={headerStyling}>Films and TV</p>
            {(data.tvBlurb) ? <p dangerouslySetInnerHTML={{ __html: data.tvBlurb }}></p> : <></>}
            <p style={subheaderStyling}>Best New To Me:</p>
            This is a list of the best films that weren't new releases that I watched for the first time in {yearString}.
            {
                data.filmsOld.map((film) => {
                    return (
                        <>
                            <p><i>{film.title}</i> ({film.year}), dir. {film.director}</p>
                        </>
                    )
                })
            }
            <hr />
            <p style={headerStyling}>Music</p>
            <p>I make playlists each year to rank my favorite albums and songs from the year. They are linked below.</p>
            <p>My favorite song of {yearString} was "{data.music.bestSong.title}" by {data.music.bestSong.artist}, and my favorite album of {yearString} was <i>{data.music.bestAlbum.title}</i> by {data.music.bestAlbum.artist}.</p>
            <GenericPlaylistWidgetEmbed playlistLink={data.music.albumsPlaylist ?? ""} />
            <GenericPlaylistWidgetEmbed playlistLink={data.music.songsPlaylist ?? ""} />

        </div>
    )
}

function PresentFeed() {
    const data = archiveData.present as YearArchiveType
    if (data === undefined) {
        return (
            <Navigate to="/feed" />
        )
    }

    const currentlyReadingStringHTML = `I'm currently reading ${data.currentlyReading.map((book: { title: string; author: string; }) => {
        return `<i>${book.title}</i> by ${book.author}`
    }).join(" and ")}.`

    return (
        <div>
            <p style={subheaderStyling}></p>
            <p>(last updated {archiveData.lastUpdated})</p>
            <p style={headerStyling}>Reading</p>
            <p dangerouslySetInnerHTML={{ __html: currentlyReadingStringHTML }} />
            <br />
            {(data.books.length > 0) ?
                <>
                    <p>The best books I've read this year:</p>
                    {
                        data.books.map((book) => {
                            return (
                                <>
                                    <p style={subheaderStyling}>
                                        <i>{book.title}</i> by {book.author}
                                    </p>
                                    <p dangerouslySetInnerHTML={{ __html: book.blurb }} />
                                </>
                            )
                        })
                    } </> : <></>
            }
            <hr />
            <p style={headerStyling}>Films and TV</p>
            {(data.tvBlurb) ? <p dangerouslySetInnerHTML={{ __html: data.tvBlurb }}></p> : <></>}
            <p style={subheaderStyling}>Best New To Me:</p>
            This is a list of the best films that weren't new releases that I watched for the first time this year.
            {
                data.filmsOld.map((film) => {
                    return (
                        <>
                            <p><i>{film.title}</i> ({film.year}), dir. {film.director}</p>
                        </>
                    )
                })
            }
            <hr />
            <p style={headerStyling}>Music</p>
            <p>I make playlists each year to rank my favorite albums and songs from the year. They are linked below.</p>
            <p>My favorite song of the year is "{data.music.bestSong.title}" by {data.music.bestSong.artist}, and my favorite album of the year is <i>{data.music.bestAlbum.title}</i> by {data.music.bestAlbum.artist}.</p>
            <GenericPlaylistWidgetEmbed playlistLink={data.music.albumsPlaylist ?? ""} />
            <GenericPlaylistWidgetEmbed playlistLink={data.music.songsPlaylist ?? ""} />

        </div>
    )
}

function FeedNav({ selectedYear }: { selectedYear: string }) {

    function feedLink(option: string) {
        if (selectedYear === option || (option === "present" && selectedYear === "NONE")) {
            return (
                <p><b>{option}</b></p>
            )
        }
        else {
            return (
                <a className="App-link" href={`/feed/${option}`}>
                    <p >{option}</p>
                </a>
            )
        }
    }

    let options: string[] = Object.keys(archiveData.archive)

    return (
        <div className="App-button-row">
            <p>View other years:</p>
            {feedLink("present")}
            {
                options.map((option) => {
                    return (
                        <>
                            {feedLink(option)}
                        </>
                    )
                })
            }
        </div>
    )
}

function Feed() {
    let params = useParams();
    let year: string = params.year ?? "NONE"

    return (
        <div className="App-body">
            <FeedNav selectedYear={year} />
            {(year === "NONE") ? <PresentFeed /> : <ArchivedFeed year={year} />}
            <img src={scribble} alt="" style={{ width: "20vh", marginTop: "5vh" }} onContextMenu={() => { return false; }} draggable={false} />
        </div>
    )


}

export default Feed;
